import React, { FunctionComponent, useState } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { sortBy } from 'lodash';

import { useToggler } from '../../../../../utils/toggler.utils';
import { TaskCard } from '../TaskCard';
import { ITasksListProps } from './types';
import { ITaskSimple } from '../../../../../app/resource/task-simple/task-simple.types';
import { useSelectWithParams } from '../../../../../utils/selector.utils';
import { taskSimpleSelectors } from '../../../../../app/resource/task-simple/task-simple.selectors';
import { TaskWidget } from '../../widget-items/task-widget-items/TaskWidget';
import { sortDoneTasksByCompletedOrChangedAt } from '../../../../../utils/task.utils';

enum Sort {
  MEETING_TITLE_ASC,
  MEETING_TITLE_DESC,
  DUEDATE_ASC,
  DUEDATE_DESC,
  NOT_SORTED,
}

export const WidgetSortableTasksList: FunctionComponent<ITasksListProps> = ({
  activeIds,
  doneIds,
  type,
  isAdmin,
  title,
}) => {
  const [show, handleShow] = useToggler(false);

  const [sort, setSort] = useState<Sort>(Sort.NOT_SORTED);
  const activeTasks = useSelectWithParams(taskSimpleSelectors.selectResourcesById, activeIds);
  const doneTasks = useSelectWithParams(taskSimpleSelectors.selectResourcesById, doneIds);

  let sortedActiveTasks: (ITaskSimple | null)[];
  let sortedDoneTasks: (ITaskSimple | null)[];

  switch (sort) {
    case Sort.MEETING_TITLE_ASC:
      sortedActiveTasks = sortBy(activeTasks, ['meetingTitle']);
      sortedDoneTasks = sortBy(doneTasks, ['meetingTitle']);
      break;
    case Sort.MEETING_TITLE_DESC:
      sortedActiveTasks = sortBy(activeTasks, ['meetingTitle']).reverse();
      sortedDoneTasks = sortBy(doneTasks, ['meetingTitle']).reverse();
      break;
    case Sort.DUEDATE_ASC:
      sortedActiveTasks = sortBy(activeTasks, ['dueDate']);
      sortedDoneTasks = sortBy(doneTasks, ['dueDate']);
      break;
    case Sort.DUEDATE_DESC:
      sortedActiveTasks = sortBy(activeTasks, ['dueDate']).reverse();
      sortedDoneTasks = sortBy(doneTasks, ['dueDate']).reverse();
      break;
    default:
      sortedActiveTasks = activeTasks;
      sortedDoneTasks = sortDoneTasksByCompletedOrChangedAt(doneTasks);
  }

  const handleSortByTitle = () => {
    switch (sort) {
      case Sort.NOT_SORTED:
        setSort(Sort.MEETING_TITLE_ASC);
        break;
      case Sort.MEETING_TITLE_ASC:
        setSort(Sort.MEETING_TITLE_DESC);
        break;
      case Sort.MEETING_TITLE_DESC:
        setSort(Sort.NOT_SORTED);
        break;
      default:
        setSort(Sort.MEETING_TITLE_ASC);
    }
  };

  const handleSortByDueDate = () => {
    switch (sort) {
      case Sort.NOT_SORTED:
        setSort(Sort.DUEDATE_ASC);
        break;
      case Sort.DUEDATE_ASC:
        setSort(Sort.DUEDATE_DESC);
        break;
      case Sort.DUEDATE_DESC:
        setSort(Sort.NOT_SORTED);
        break;
      default:
        setSort(Sort.DUEDATE_ASC);
    }
  };

  const printMeetingTitleSortDirectionIcon = () => {
    switch (sort) {
      case Sort.MEETING_TITLE_ASC:
        return <i className="icon-sort-alt-up"></i>;
      case Sort.MEETING_TITLE_DESC:
        return <i className="icon-sort-alt-down"></i>;
      default:
        return null;
    }
  };

  const printDueDateSortDirectionIcon = () => {
    switch (sort) {
      case Sort.DUEDATE_ASC:
        return <i className="icon-sort-alt-up"></i>;
      case Sort.DUEDATE_DESC:
        return <i className="icon-sort-alt-down"></i>;
      default:
        return null;
    }
  };

  return (
    <div className="single-card-container h100">
      <div className="panel-body p-0 h100">
        <div
          className="border-radius-1 p-2 pb-1 h100"
          style={isAdmin ? { backgroundColor: '#f6f9fb' } : { backgroundColor: '#eff1f3' }}
        >
          {!!title && (
            <div className="panel-header p-0">
              <h2 className="bold">{title}</h2>
              <div className="ml-auto flex"></div>
            </div>
          )}
          {(sortedActiveTasks.length > 0 || sortedDoneTasks.length > 0) && (
            <div className="flex justify-between py-2 align-center">
              <div>Sort by:</div>
              <div className="flex">
                <p onClick={handleSortByTitle} style={{ width: 80, cursor: 'pointer' }}>
                  Meetings{printMeetingTitleSortDirectionIcon()}
                </p>
                <p onClick={handleSortByDueDate} style={{ width: 75, cursor: 'pointer' }}>
                  Due date{printDueDateSortDirectionIcon()}
                </p>
              </div>
            </div>
          )}
          <div className="todo-container tabs">
            <Droppable droppableId={`active`} isDropDisabled={true}>
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {sortedActiveTasks.map(
                    (task, index) =>
                      task && (
                        <Draggable key={`active-${task.id}`} draggableId={task.id} index={index} isDragDisabled={true}>
                          {(provided) => {
                            if (type === 'qi') {
                              return <TaskWidget task={task} provided={provided} />;
                            }
                            return <TaskCard task={task} provided={provided} meetingId="" />;
                          }}
                        </Draggable>
                      ),
                  )}
                  {!activeIds.length && <div style={{ height: '20px' }}></div>}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>

            <div className="hidden_cards_container">
              <span className="card-separator hide-cards" onClick={handleShow}>
                <span className="completed_text">
                  {show ? `HIDE COMPLETED (${doneIds.length})` : `SHOW COMPLETED (${doneIds.length})`}
                </span>
              </span>
              {show && (
                <Droppable droppableId={'done'} isDropDisabled={true}>
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {sortedDoneTasks.map(
                        (task, index) =>
                          task && (
                            <Draggable key={`done-${task.id}`} draggableId={task.id} index={index}>
                              {(provided) => {
                                if (type === 'qi') {
                                  return <TaskWidget task={task} provided={provided} />;
                                }
                                return <TaskCard task={task} provided={provided} meetingId="" />;
                              }}
                            </Draggable>
                          ),
                      )}
                      {!doneIds.length && <div style={{ height: '20px' }}></div>}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
